import { useCallback, useEffect, useRef, useState } from 'react';

/** Maps selection_id -> current odds value */
export type OddsMap = Record<string, number>;

interface OddsStreamMessage {
  selection_id: string;
  odds: number;
}

const WS_RECONNECT_DELAY_MS = 3000;
const WS_MAX_RECONNECTS = 10;

/**
 * Connects a WebSocket to ws://{host}/ws/odds/{event_id} and returns a live
 * map of selection_id -> odds.  Reconnects automatically on disconnect.
 */
export function useOddsStream(eventId: string | undefined): OddsMap {
  const [oddsMap, setOddsMap] = useState<OddsMap>({});
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCount = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmounted = useRef(false);

  const connect = useCallback(() => {
    if (!eventId || unmounted.current) return;
    if (reconnectCount.current >= WS_MAX_RECONNECTS) return;

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${protocol}://${window.location.host}/ws/odds/${eventId}`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onmessage = (ev) => {
      try {
        const data: OddsStreamMessage = JSON.parse(ev.data as string);
        if (data.selection_id && typeof data.odds === 'number') {
          setOddsMap((prev) => ({ ...prev, [data.selection_id]: data.odds }));
        }
      } catch {
        // ignore malformed frames
      }
    };

    ws.onclose = () => {
      if (unmounted.current) return;
      reconnectCount.current += 1;
      reconnectTimer.current = setTimeout(connect, WS_RECONNECT_DELAY_MS);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [eventId]);

  useEffect(() => {
    unmounted.current = false;
    reconnectCount.current = 0;
    setOddsMap({});
    connect();

    return () => {
      unmounted.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on intentional close
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return oddsMap;
}
